import type { OnboardingResource } from "@shared/schema";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { orpc } from "@/lib/rpc";
import type { AssignmentWithResource, MilestonesDataSource } from "./data-source";

// Template-side data source: reads/writes the template_* tables. Used by
// PG-TPL-CANVAS (UC-TPL-013..022, UC-TPL-017..021).

export function useTemplateMilestonesDataSource(
  templateId: string,
  options: { isOwner: boolean },
): MilestonesDataSource {
  const queryClient = useQueryClient();
  const [pickerEnabled, setPickerEnabled] = useState(false);

  const assignmentsQueryOptions = orpc.onboardingTemplateAssignments.list.queryOptions({
    input: { templateId },
  });
  const assignmentsQuery = useQuery({
    ...assignmentsQueryOptions,
    enabled: !!templateId,
  });
  const assignments = (assignmentsQuery.data as AssignmentWithResource[] | undefined) ?? [];

  const resourcesQueryOptions = orpc.onboardingResources.list.queryOptions({
    input: { templateId },
  });
  const resourcesQuery = useQuery({
    ...resourcesQueryOptions,
    enabled: !!templateId && pickerEnabled,
  });
  const resources = (resourcesQuery.data as OnboardingResource[] | undefined) ?? [];

  const addMutation = useMutation(orpc.onboardingTemplateAssignments.add.mutationOptions());
  const moveMutation = useMutation(orpc.onboardingTemplateAssignments.move.mutationOptions());
  const setScopeMutation = useMutation(
    orpc.onboardingTemplateAssignments.setScope.mutationOptions(),
  );
  const removeMutation = useMutation(orpc.onboardingTemplateAssignments.remove.mutationOptions());

  const invalidate = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: assignmentsQueryOptions.queryKey }),
      queryClient.invalidateQueries({ queryKey: resourcesQueryOptions.queryKey }),
      // Preview pages (orpc.preview.getTemplatePreview) read assignments/resources
      // alongside the template row, so they must refetch whenever the canvas changes.
      queryClient.invalidateQueries({
        queryKey: orpc.preview.getTemplatePreview.queryOptions().queryKey,
      }),
    ]);
  }, [queryClient, assignmentsQueryOptions.queryKey, resourcesQueryOptions.queryKey]);

  const isMutationPending =
    addMutation.isPending ||
    moveMutation.isPending ||
    setScopeMutation.isPending ||
    removeMutation.isPending;

  return useMemo<MilestonesDataSource>(
    () => ({
      assignments,
      resources,
      isLoading: assignmentsQuery.isLoading,
      isMutationPending,
      pickerEnabled,
      setPickerEnabled,

      async addAssignment(input) {
        if (!options.isOwner) return null;
        const created = await addMutation.mutateAsync({ ...input, templateId });
        return (created as AssignmentWithResource) ?? null;
      },
      async moveAssignment(input) {
        if (!options.isOwner) return;
        await moveMutation.mutateAsync(input);
      },
      async setScope(input) {
        if (!options.isOwner) return;
        await setScopeMutation.mutateAsync(input);
      },
      async removeAssignment(input) {
        if (!options.isOwner) return;
        await removeMutation.mutateAsync(input);
      },

      invalidate,
    }),
    [
      assignments,
      resources,
      assignmentsQuery.isLoading,
      isMutationPending,
      pickerEnabled,
      addMutation,
      moveMutation,
      setScopeMutation,
      removeMutation,
      invalidate,
      templateId,
      options.isOwner,
    ],
  );
}
